import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';

import { DataEndChannel } from 'src/app/project/shared/interfaces/data-end-channel.interface';
import { ProjectRouteStationWithParam } from 'src/app/project/shared/interfaces/project-route-station.class';
import { Project } from 'src/app/project/shared/interfaces/project.interface';
import { ProjectGroupMetaInformation } from 'src/app/project/shared/interfaces/project-group-meta-information';
import { ProjectDataService } from 'src/app/project/shared/services/project-data.service';
import { MetaInformationService } from 'src/app/project/shared/services/meta-information.service';
import { DataEndChannelService } from 'src/app/project/shared/services/data-end-channel.service';
import { EngineParameterEditService } from 'src/app/project/shared/services/engine-parameter-edit.service';
import { getMetaInformationLabel } from 'src/app/project/shared/functions/meta-information-label';
import { getPositionPiktogramm } from 'src/app/project/shared/functions/position-piktogramm';
import { getChannelLabel, getChannelRole, ChannelLabel, ChannelRole } from 'src/app/project/shared/functions/channel-label';
import { getLineColor } from 'src/app/project/shared/functions/line-color';
import { buildStationColumns, StationColumn, StationColumns } from 'src/app/project/shared/functions/station-columns';
import { AlertService } from 'src/app/shared/alert/shared/alert.service';
import { AlertTypes } from 'src/app/shared/alert/shared/alert-types';
import { UserDataService } from 'src/app/user/shared/services/user-data.service';
import { RoleTypes } from 'src/app/user/shared/enums/role.enum';
import { ProjectGroup } from 'src/app/project/shared/interfaces/project-group.interface';
import { GroupService } from 'src/app/project/shared/services/group.service';
import { SelectInput } from 'src/app/shared/dropdown/shared/select-input.class';
import { GroupTypeEnum, getGroupTypeSelect, getGroupTypeString } from 'src/app/project/shared/enums/group-type.enum';
import { DiagramTypeEnum, getDiagramTypeSelect, getDiagramTypeString } from 'src/app/project/shared/enums/diagram-type.enum';
import { TrafficRulesEnum, getTrafficRulesSelect, getTrafficRulesString } from 'src/app/project/shared/enums/traffic-rules.enum';
import { GroupMetaData, GroupMetaDataTypes } from 'src/app/project/project-settings/shared/interfaces/group-meta.interface';

interface LegendEntry {
  line: number;
  color: string;
  count: number;
}

/** Verbindungspfeil von einem steuernden zu einem gesteuerten Element (gleiche Line). */
interface Connector {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

@Component({
  selector: 'avs-project-swa-visualisierung',
  templateUrl: './project-swa-visualisierung.component.html',
  styleUrls: ['./project-swa-visualisierung.component.css']
})
export class ProjectSwaVisualisierungComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Breite (px) einer Station-Karte inkl. Abstand – bestimmt die Spaltenbreite. */
  private static readonly CARD_WIDTH = 244;

  @ViewChild('track') private trackRef?: ElementRef<HTMLElement>;

  public board: StationColumns = { columns: [], unassigned: [] };
  public legend: LegendEntry[] = [];

  // Verbindungspfeile (Wirkung Quelle -> Ziel) der aktuell hervorgehobenen Line
  public connectors: Connector[] = [];
  public connectorColor = '';
  public trackSize = { w: 0, h: 0 };
  public metaInformation: ProjectGroupMetaInformation[] = [];

  public mode: 'view' | 'edit' = 'view';
  public selectedLine: number | null = null;         // Ansicht: Hervorhebung
  public activeLine: number | 'erase' | null = null;  // Bearbeiten: aktiver Pinsel

  public isEditor = false;   // nur Voll-Admin (Rolle 1) darf bearbeiten
  public projectId?: number;

  // ---------- Gruppen-Parameter (wie in der alten SWA-Lösung) ----------
  public activeGroup?: ProjectGroup;
  public trafficLanes = 2;
  public isTrafficLanesChanged = false;
  public groupMetaData?: GroupMetaData;
  public isGroupTypesSelectOpen = false;
  public isDiagramTypesSelectOpen = false;
  public isTrafficRulesSelectOpen = false;
  public groupTypeSelect: SelectInput[] = getGroupTypeSelect();
  public diagramTypeSelect: SelectInput[] = getDiagramTypeSelect();
  public trafficRulesSelect: SelectInput[] = getTrafficRulesSelect();
  public readonly GroupMetaDataTypes = GroupMetaDataTypes;
  public readonly getGroupTypeString = getGroupTypeString;
  public readonly getDiagramTypeString = getDiagramTypeString;
  public readonly getTrafficRulesString = getTrafficRulesString;

  private routeStations: ProjectRouteStationWithParam[] = [];
  private originalLines = new Map<number, number | null>();  // Snapshot für Abbrechen
  private subscriptions: Subscription[] = [];

  constructor(
    private readonly projectDataService: ProjectDataService,
    private readonly metaInfoService: MetaInformationService,
    private readonly dataEndChannelService: DataEndChannelService,
    private readonly engineParameterEditService: EngineParameterEditService,
    private readonly userDataService: UserDataService,
    private readonly alertService: AlertService,
    private readonly groupService: GroupService
  ) { }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.projectDataService.projectsRouteStationsWithParam.subscribe(stations => {
        this.routeStations = stations ?? [];
        this.board = buildStationColumns(this.routeStations);
        this.rebuildLegend();
        this.scheduleConnectorUpdate();
      }),
      this.projectDataService.activeGroup.subscribe(group => {
        this.activeGroup = group;
        this.isTrafficLanesChanged = false;
        this.groupMetaData = undefined;
        if (group) { this.trafficLanes = group.trafficLanes; this.loadMetaInformation(group.id); }
        else { this.metaInformation = []; }
      }),
      this.projectDataService.activeProject.subscribe((project: Project | undefined) => {
        this.projectId = project?.id;
      }),
      this.userDataService.userData.subscribe(user => {
        this.isEditor = user?.role === RoleTypes.admin;
      })
    );
    // Beim Öffnen frische Daten laden, damit Änderungen aus dem Einstellungen-Tab sichtbar sind.
    this.projectDataService.reloadActiveGroupRouteStations();
  }

  public ngAfterViewInit(): void {
    this.scheduleConnectorUpdate();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    this.scheduleConnectorUpdate();
  }

  /** Nach dem Rendern (Layout steht) die Verbindungspfeile neu berechnen. */
  private scheduleConnectorUpdate(): void {
    setTimeout(() => this.updateConnectors());
  }

  private updateConnectors(): void {
    const track = this.trackRef?.nativeElement;
    if (!track || this.mode !== 'view' || this.selectedLine == null) {
      this.connectors = [];
      return;
    }
    this.trackSize = { w: track.offsetWidth, h: track.offsetHeight };
    const trackRect = track.getBoundingClientRect();
    const box = (el: HTMLElement) => {
      const r = el.getBoundingClientRect();
      return { cx: r.left - trackRect.left + r.width / 2, cy: r.top - trackRect.top + r.height / 2, w: r.width };
    };

    const chips = Array.from(track.querySelectorAll<HTMLElement>('.avs-swa-chip'))
      .filter(chip => chip.dataset['line'] === String(this.selectedLine));
    const sources = chips.filter(chip => chip.dataset['role'] === 'source').map(box);
    const targets = chips.filter(chip => chip.dataset['role'] === 'target').map(box);

    const connectors: Connector[] = [];
    const spread = (count: number, index: number, width: number) => {
      const step = Math.min(30, (width - 16) / (count + 1));
      return (index - (count - 1) / 2) * step;
    };
    targets.forEach((target, targetIndex) => {
      sources.forEach((source, sourceIndex) => {
        // Ziel- und Startpunkt über die Kartenbreite verteilen, damit sich mehrere Pfeile nicht überlagern
        const x2 = target.cx + spread(sources.length, sourceIndex, target.w);
        const x1 = source.cx + spread(targets.length, targetIndex, source.w);
        connectors.push({ x1, y1: source.cy, x2, y2: target.cy });
      });
    });
    this.connectors = connectors;
    this.connectorColor = getLineColor(this.selectedLine);
  }

  // ---------- Positions-Beschriftung (Metainformationen wie im Schema-Tab) ----------
  private loadMetaInformation(groupId: number): void {
    this.subscriptions.push(
      this.metaInfoService.getMetaInformationByGroupId(groupId).subscribe((info: ProjectGroupMetaInformation[]) => {
        this.metaInformation = info ?? [];
      })
    );
  }

  // ---------- Gruppen-Parameter setzen (analog zur alten SWA-Lösung) ----------
  public onGetActiveSelect(type: GroupMetaDataTypes, value: number): SelectInput {
    switch (type) {
      case GroupMetaDataTypes.groupType: return { name: getGroupTypeString(value), id: value } as SelectInput;
      case GroupMetaDataTypes.diagramType: return { name: getDiagramTypeString(value), id: value } as SelectInput;
      case GroupMetaDataTypes.trafficRules: return { name: getTrafficRulesString(value), id: value } as SelectInput;
      default: return {} as SelectInput;
    }
  }

  public onMetaValueChange(selected: SelectInput, type: GroupMetaDataTypes): void {
    if (!this.activeGroup) { return; }
    if (!this.groupMetaData) {
      this.groupMetaData = { name: undefined, routeName: undefined, groupType: undefined, diagramType: undefined, isActive: undefined, trafficRules: undefined };
    }
    // Dropdown-Ids sind die numerischen Enum-Werte (siehe get*Select()).
    const id = Number(selected.id);
    switch (type) {
      case GroupMetaDataTypes.groupType:
        this.groupMetaData.groupType = id as GroupTypeEnum;
        this.activeGroup.groupType = id as GroupTypeEnum;
        this.isGroupTypesSelectOpen = false;
        break;
      case GroupMetaDataTypes.diagramType:
        this.groupMetaData.diagramType = id as DiagramTypeEnum;
        this.activeGroup.diagramType = id as DiagramTypeEnum;
        this.isDiagramTypesSelectOpen = false;
        break;
      case GroupMetaDataTypes.trafficRules:
        this.groupMetaData.trafficRules = id as TrafficRulesEnum;
        this.activeGroup.trafficRules = id as TrafficRulesEnum;
        this.isTrafficRulesSelectOpen = false;
        break;
    }
  }

  public onGroupTypeSelectClicked(): void {
    this.isGroupTypesSelectOpen = !this.isGroupTypesSelectOpen;
    this.isDiagramTypesSelectOpen = false;
    this.isTrafficRulesSelectOpen = false;
  }
  public onDiagramTypeSelectClicked(): void {
    this.isDiagramTypesSelectOpen = !this.isDiagramTypesSelectOpen;
    this.isGroupTypesSelectOpen = false;
    this.isTrafficRulesSelectOpen = false;
  }
  public onTrafficRulesSelectClicked(): void {
    this.isTrafficRulesSelectOpen = !this.isTrafficRulesSelectOpen;
    this.isGroupTypesSelectOpen = false;
    this.isDiagramTypesSelectOpen = false;
  }

  public changeTrafficLane(newValue: number): void {
    this.trafficLanes = newValue;
    this.isTrafficLanesChanged = true;
  }

  public onSaveMetaDataClicked(): void {
    if (!this.activeGroup) { return; }
    this.activeGroup.trafficLanes = this.trafficLanes;
    this.subscriptions.push(
      this.groupService.editProjectGroup(this.activeGroup.id, this.activeGroup).subscribe(() => {
        this.alertService.alert('Gruppen-Einstellungen wurden gespeichert', AlertTypes.info);
        this.groupMetaData = undefined;
        this.isTrafficLanesChanged = false;
      })
    );
  }

  public getPositionLabel(station: ProjectRouteStationWithParam): string {
    return getMetaInformationLabel(this.metaInformation, station.routeStation.x, station.routeStation.y);
  }

  public getPositionPiktogramm(station: ProjectRouteStationWithParam): string | null {
    const meta = this.metaInformation.find(m => m.x === station.routeStation.x && m.y === station.routeStation.y);
    return getPositionPiktogramm(meta?.descriptionType);
  }

  // ---------- Komponenten / Farben ----------
  public visibleChannels(station: ProjectRouteStationWithParam): DataEndChannel[] {
    const radars = station.schemaInfo?.radars;
    return (station.routeStation.dataEndChannels ?? [])
      .filter(channel => getChannelLabel(channel, radars) !== null)
      .sort((a, b) => a.fg - b.fg || a.de - b.de);
  }

  public getLabel(station: ProjectRouteStationWithParam, channel: DataEndChannel): ChannelLabel | null {
    return getChannelLabel(channel, station.schemaInfo?.radars);
  }
  public getColor(line: number | null | undefined): string { return getLineColor(line); }
  public getRole(channel: DataEndChannel): ChannelRole | null { return getChannelRole(channel); }

  private rebuildLegend(): void {
    const counts = new Map<number, number>();
    for (const station of this.routeStations) {
      const radars = station.schemaInfo?.radars;
      for (const channel of station.routeStation.dataEndChannels ?? []) {
        if (channel.line != null && getChannelLabel(channel, radars)) {
          counts.set(channel.line, (counts.get(channel.line) ?? 0) + 1);
        }
      }
    }
    this.legend = [...counts.keys()].sort((a, b) => a - b)
      .map(line => ({ line, color: getLineColor(line), count: counts.get(line) ?? 0 }));
    if (this.selectedLine != null && !counts.has(this.selectedLine)) { this.selectedLine = null; }
  }

  // ---------- Hervorhebung ----------
  private get effectiveLine(): number | null {
    if (this.mode === 'edit') { return this.activeLine === 'erase' ? null : this.activeLine; }
    return this.selectedLine;
  }
  public isDimmed(channel: DataEndChannel): boolean {
    return this.effectiveLine != null && channel.line !== this.effectiveLine;
  }
  public isHighlighted(channel: DataEndChannel): boolean {
    return this.effectiveLine != null && channel.line === this.effectiveLine;
  }

  public onLegendClick(line: number): void {
    if (this.mode === 'edit') { this.activeLine = line; }
    else { this.selectedLine = this.selectedLine === line ? null : line; this.scheduleConnectorUpdate(); }
  }
  public clearSelection(): void { this.selectedLine = null; this.scheduleConnectorUpdate(); }

  public get selectedCount(): number {
    const entry = this.legend.find(item => item.line === this.selectedLine);
    return entry ? entry.count : 0;
  }

  // ---------- Bearbeiten-Modus (Line-Zuordnung) ----------
  public get brushLines(): number[] {
    const used = this.legend.map(entry => entry.line);
    const max = used.length ? Math.max(...used) : 0;
    const upper = Math.min(15, max + 1);
    const lines: number[] = [];
    for (let n = 1; n <= upper; n++) { lines.push(n); }
    return lines;
  }
  public isNewBrushLine(line: number): boolean { return !this.legend.some(entry => entry.line === line); }
  public isActiveBrush(value: number | 'erase'): boolean { return this.activeLine === value; }
  public setEraser(): void { this.activeLine = 'erase'; }

  public get hasPendingChanges(): boolean {
    for (const [id, original] of this.originalLines) {
      const channel = this.findChannel(id);
      if (channel && channel.line !== original) { return true; }
    }
    return false;
  }

  public enterEditMode(): void {
    this.mode = 'edit';
    this.selectedLine = null;
    this.activeLine = this.legend.length ? this.legend[0].line : 1;
    this.originalLines.clear();
    this.scheduleConnectorUpdate();
  }

  public cancelEditMode(): void {
    for (const [id, original] of this.originalLines) {
      const channel = this.findChannel(id);
      if (channel) { channel.line = original; }
    }
    this.originalLines.clear();
    this.exitEditMode();
  }

  public saveEditMode(): void {
    const changed: DataEndChannel[] = [];
    for (const [id, original] of this.originalLines) {
      const channel = this.findChannel(id);
      if (channel && channel.line !== original) { changed.push(channel); }
    }
    if (changed.length === 0) { this.exitEditMode(); return; }
    this.subscriptions.push(
      forkJoin(changed.map(channel => this.dataEndChannelService.editDataEndChannel(channel.id, channel)))
        .subscribe({
          next: () => {
            this.alertService.alert('Line-Zuordnungen wurden gespeichert', AlertTypes.info);
            this.originalLines.clear();
            this.projectDataService.reloadActiveRouteStationChannels();
            this.exitEditMode();
          },
          error: () => this.alertService.alert('Fehler beim Speichern der Line-Zuordnungen', AlertTypes.error)
        })
    );
  }

  private exitEditMode(): void {
    this.mode = 'view';
    this.activeLine = null;
    this.selectedLine = null;
    this.rebuildLegend();
    this.scheduleConnectorUpdate();
  }

  // ---------- Klick auf eine Komponente ----------
  public onChannelClick(station: ProjectRouteStationWithParam, channel: DataEndChannel): void {
    if (this.mode === 'edit') { this.assignLine(channel); return; }
    if (!this.engineParameterEditService.hasEngineParameters(channel)) { return; }
    // gleicher Dialog für Admin (editierbar) und User (nur lesend)
    this.subscriptions.push(
      this.engineParameterEditService
        .openEditor(channel, station.routeStation.id, this.projectId, !this.isEditor)
        .subscribe(updated => {
          // Nach dem Speichern die DataEndChannel-Liste (Einstellungen-Tab) mit-aktualisieren.
          if (updated) { this.projectDataService.reloadActiveRouteStationChannels(); }
        })
    );
  }

  private assignLine(channel: DataEndChannel): void {
    if (!this.originalLines.has(channel.id)) { this.originalLines.set(channel.id, channel.line); }
    if (this.activeLine === 'erase') {
      channel.line = null;
    } else {
      channel.line = channel.line === this.activeLine ? null : (this.activeLine as number);
    }
    this.rebuildLegend();
  }

  private findChannel(id: number): DataEndChannel | undefined {
    for (const station of this.routeStations) {
      const found = (station.routeStation.dataEndChannels ?? []).find(channel => channel.id === id);
      if (found) { return found; }
    }
    return undefined;
  }

  /** Spaltenbreite = Anzahl der Stationen auf der stärker besetzten Seite × Kartenbreite. */
  public columnWidth(column: StationColumn): number {
    const count = Math.max(column.top.length, column.bottom.length, 1);
    return count * ProjectSwaVisualisierungComponent.CARD_WIDTH;
  }

  public trackByColumnX(_index: number, column: StationColumn): number {
    return column.x;
  }
  public trackByStationId(_index: number, station: ProjectRouteStationWithParam): string {
    return station.routeStation.id;
  }
  public trackByChannelId(_index: number, channel: DataEndChannel): number {
    return channel.id;
  }
}
